import { z } from 'zod';
import { RENTAL_BILLING_INTERVALS, RENTAL_RETURN_SCAN_RESULTS, RENTAL_RETURN_SCAN_STEPS, RENTAL_STATUSES, } from '../constants/rental-states.js';
import { unknownRentalPlaceholders } from '../lib/rental-templates.js';
import { emailSchema, moneyCentsSchema, uuidSchema } from './common.js';
/** Day-only, 'yyyy-mm-dd' — a rental's dates don't carry a time of day. */
export const rentalDateSchema = z.iso.date();
/** `check`/`zelle` (t0942) are manual-record only, same as `cash`/`external_card` —
 *  the order payment schema (order.ts) keeps its own narrower enum, so this
 *  widening doesn't reach order checkout. */
const paymentProviderSchema = z.enum(['stripe', 'cash', 'external_card', 'check', 'zelle']);
/** A damage fee deducted from the held deposit (t0944) — never something
 *  staff hand-pick, only ever written by `recordDamageFeeSchema`'s own
 *  service path, which is why it's absent from `paymentProviderSchema`. */
const rentalPaymentRowProviderSchema = z.enum([
    'stripe',
    'cash',
    'external_card',
    'check',
    'zelle',
    'deposit',
    // The shared database enum also carries these (t1102); a rental payment can
    // never be one, but the response type has to admit what the column can hold.
    'gift_card',
    'store_credit',
]);
/** The narrow, staff-facing kind — `damage_fee` (t0944) is deliberately left
 *  out, since it can only be created through `recordDamageFeeSchema`'s own
 *  endpoint, never the generic record-payment one. */
const rentalPaymentKindSchema = z.enum(['fee', 'deposit', 'deposit_refund']);
/** Every kind a payment row can actually carry — widens the input-only
 *  `rentalPaymentKindSchema` with `damage_fee` for responses. */
const rentalPaymentRowKindSchema = z.enum(['fee', 'deposit', 'deposit_refund', 'damage_fee']);
/** `void` (t0942) is a staff correction for a payment that really happened
 *  and was later reversed — distinct from `failed`/`refunded`, which come
 *  from the Stripe charging flow (t0941). A voided payment stays in history
 *  but must never count as collected. `authorized`/`canceled` (t0951) are a
 *  card authorization hold in place, and one released without ever being
 *  captured — an expired or failed hold lands on `failed` instead, since it's
 *  an actionable problem staff need to recover from, not a neutral outcome. */
const rentalPaymentStatusSchema = z.enum([
    'pending',
    'succeeded',
    'failed',
    'refunded',
    'partially_refunded',
    'void',
    'authorized',
    'canceled',
]);
/** Only fee/deposit/damage_fee are ever charged — a deposit refund is money
 *  going out, never something the portal charges a card for. */
const chargeableRentalPaymentKindSchema = z.enum(['fee', 'deposit', 'damage_fee']);
const needsAttentionFilter = z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
    .optional();
export const rentalListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(50),
    q: z.string().trim().max(200).optional(),
    /** 'except_cancelled' matches every status but cancelled (t1056). */
    status: z.enum([...RENTAL_STATUSES, 'except_cancelled']).optional(),
    customerId: uuidSchema.optional(),
    /** Rentals opened alongside a purchase in the same counter checkout
     *  (t0943) — the order's own detail page uses this to show its rentals. */
    orderId: uuidSchema.optional(),
    /** Together with `to`, the date range the admin calendar is showing. */
    from: rentalDateSchema.optional(),
    to: rentalDateSchema.optional(),
    /** Which date `from`/`to` filter on. `either` matches a rental whose start
     *  or due date falls in range — an open-ended rental has no due date, so it
     *  only ever matches on its start. */
    dateField: z.enum(['start', 'due', 'either']).default('either'),
    /** Only rentals a return scan flagged (t1105). */
    needsAttention: needsAttentionFilter,
});
/** One serialized unit attached to a rental. A rental can carry several —
 *  `releasedAt` is null while the unit is still out. */
export const rentalItemSchema = z.object({
    id: uuidSchema,
    serializedUnitId: uuidSchema,
    imei: z.string(),
    serialNumber: z.string().nullable(),
    productId: uuidSchema,
    productName: z.string(),
    /** So the UI can label the identifier IMEI vs ICCID via `unitIdentifierKind`. */
    productType: z.string(),
    addedAt: z.iso.datetime(),
    releasedAt: z.iso.datetime().nullable(),
    /** Set when staff scanned this exact unit back in (t1105). */
    verifiedAt: z.iso.datetime().nullable(),
    /** Current shelf price of the product — what a "not returned" charge
     *  defaults to (t1255). */
    productPriceCents: moneyCentsSchema,
});
export const rentalListItemSchema = z.object({
    id: uuidSchema,
    rentalNumber: z.string(),
    status: z.enum(RENTAL_STATUSES),
    customerId: uuidSchema,
    customerName: z.string().nullable(),
    customerPhone: z.string().nullable(),
    /** The first attached item (earliest added) — enough for the list column;
     *  the full set is on `rentalDetailSchema.items`. */
    imei: z.string(),
    productName: z.string(),
    productType: z.string(),
    itemCount: z.number().int().min(1),
    startDate: rentalDateSchema,
    dueDate: rentalDateSchema.nullable(),
    returnedAt: z.iso.datetime().nullable(),
    cancelledAt: z.iso.datetime().nullable(),
    notReturnedAt: z.iso.datetime().nullable(),
    feeCents: moneyCentsSchema,
    billingInterval: z.enum(RENTAL_BILLING_INTERVALS),
    /** Billed periods and the whole fee (rate x periods) — t1366. */
    feePeriods: z.number().int().min(1),
    feeTotalCents: moneyCentsSchema,
    depositCents: moneyCentsSchema,
    depositReturnedAt: z.iso.datetime().nullable(),
    depositKeptAt: z.iso.datetime().nullable(),
    /** Return verification (t1105): the customer's kiosk scan, staff's receipt
     *  scan, and the flag a mismatched item scan raises. */
    returnAnnouncedAt: z.iso.datetime().nullable(),
    returnReceivedAt: z.iso.datetime().nullable(),
    needsAttentionAt: z.iso.datetime().nullable(),
    needsAttentionReason: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const rentalPaymentSchema = z.object({
    id: uuidSchema,
    kind: rentalPaymentRowKindSchema,
    provider: rentalPaymentRowProviderSchema,
    status: rentalPaymentStatusSchema,
    amountCents: moneyCentsSchema,
    /** Set once a deposit hold is captured — null while it's only authorized.
     *  Less than `amountCents` on a partial capture, where the remainder was
     *  released back to the card (t0951). */
    capturedCents: moneyCentsSchema.nullable(),
    capturedAt: z.iso.datetime().nullable(),
    reference: z.string().nullable(),
    note: z.string().nullable(),
    /** Whose saved card was charged, when it belongs to someone other than the
     *  renter — a linked family member (t1117). Null for the renter's own card
     *  and for every non-card payment. */
    cardOwnerName: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const rentalDetailSchema = rentalListItemSchema.extend({
    notes: z.string().nullable(),
    payments: z.array(rentalPaymentSchema),
    items: z.array(rentalItemSchema),
    /** True only for a cancelled rental with no payments and no history beyond
     *  its creation and cancellation — eligible for permanent deletion. */
    deletable: z.boolean(),
    /** Set when this rental was opened alongside a purchase in the same
     *  counter checkout (t0943) — null for a rental created on its own. */
    orderId: uuidSchema.nullable(),
    orderNumber: z.string().nullable(),
    /** How much of the held deposit has been deducted for damage, and how much
     *  is still available to deduct or refund (t0944) — zero once the deposit
     *  has been resolved via return/cancel/hold-capture. Derived, not stored. */
    depositAppliedCents: moneyCentsSchema,
    depositAvailableCents: moneyCentsSchema,
});
export const rentalEventSchema = z.object({
    id: uuidSchema,
    fromStatus: z.enum(RENTAL_STATUSES).nullable(),
    toStatus: z.enum(RENTAL_STATUSES),
    actorType: z.enum(['staff', 'customer', 'kiosk', 'system']),
    actorLabel: z.string().nullable(),
    note: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
const recordPaymentInputSchema = z.object({
    provider: paymentProviderSchema,
    amountCents: moneyCentsSchema,
    reference: z.string().trim().max(200).nullable().default(null),
});
export const createRentalSchema = z.object({
    customerId: uuidSchema,
    serializedUnitId: uuidSchema,
    startDate: rentalDateSchema,
    dueDate: rentalDateSchema.nullable().default(null),
    feeCents: moneyCentsSchema,
    billingInterval: z.enum(RENTAL_BILLING_INTERVALS).default('one_time'),
    depositCents: moneyCentsSchema.default(0),
    notes: z.string().trim().max(2000).nullable().default(null),
    /** Optional fee/deposit recorded at the moment the rental is created. */
    initialPayments: z
        .array(recordPaymentInputSchema.extend({ kind: rentalPaymentKindSchema }))
        .default([]),
});
export const transitionRentalSchema = z.object({
    to: z.enum(RENTAL_STATUSES),
    note: z.string().trim().max(500).nullable().default(null),
});
export const returnRentalSchema = z.object({
    /** Per-item disposition: back on the shelf, or off for a recheck/wipe
     *  before it's sellable again. Any open item left out of this list defaults
     *  to `sellable`. */
    items: z
        .array(z.object({
        itemId: uuidSchema,
        disposition: z.enum(['sellable', 'provisioning']),
    }))
        .default([]),
    depositReturned: z.boolean().default(false),
    depositRefund: recordPaymentInputSchema.nullable().default(null),
    note: z.string().trim().max(500).nullable().default(null),
});
/** Return some of a rental's open units early (t1259); the rental stays open. */
export const returnRentalItemsSchema = z.object({
    items: z
        .array(z.object({
        itemId: uuidSchema,
        disposition: z.enum(['sellable', 'provisioning']),
    }))
        .min(1)
        .max(50),
    note: z.string().trim().max(500).nullable().default(null),
});
/** Attach one or more available units to an already-open rental. */
export const addRentalItemsSchema = z.object({
    unitIds: z.array(uuidSchema).min(1).max(50),
});
/**
 * Cancels an open rental outright: every attached unit goes straight back to
 * `sellable`, and staff choose whether the deposit (if any, and not already
 * returned) is refunded now or kept — either way the choice is recorded.
 */
export const cancelRentalSchema = z.object({
    deposit: z.enum(['refund', 'keep']).default('keep'),
    depositRefund: recordPaymentInputSchema.nullable().default(null),
    note: z.string().trim().max(500).nullable().default(null),
});
/**
 * Closes an open rental as never brought back (t1255): every open unit is
 * written off to `lost`, and staff record what happens to the deposit — same
 * choice as a cancellation. The replacement charge itself is recorded first,
 * as an ordinary damage fee.
 */
export const markRentalNotReturnedSchema = cancelRentalSchema;
/** Undoes a return marked by mistake (t1254). */
export const reopenRentalSchema = z.object({
    note: z.string().trim().max(500).nullable().default(null),
});
export const recordRentalPaymentSchema = recordPaymentInputSchema.extend({
    kind: rentalPaymentKindSchema,
    note: z.string().trim().max(500).nullable().default(null),
});
/**
 * A damage fee against an open rental (t0944) — a reason is mandatory, unlike
 * every other manual payment, since this money is being taken for a specific
 * cause rather than just collected. `mode: 'deposit'` deducts from whatever
 * is still available on the held deposit, capped there — `provider` is only
 * required when the fee is being charged outright (`mode: 'charge'`) or when
 * it exceeds what the deposit can cover, in which case it pays the remainder.
 */
export const recordDamageFeeSchema = z.object({
    amountCents: moneyCentsSchema.min(1),
    note: z.string().trim().min(1, 'A reason is required').max(500),
    mode: z.enum(['deposit', 'charge']),
    provider: paymentProviderSchema.nullable().default(null),
    reference: z.string().trim().max(200).nullable().default(null),
});
/**
 * Integrated card charging (t0941) — three ways to charge a rental fee or
 * deposit, mirroring the order checkout / saved-card stack rather than
 * inventing new shapes: a newly entered card or a connected reader both open
 * a PaymentIntent (`createRentalCardIntentSchema`), a saved card charges
 * off-session in one call (`chargeRentalSavedCardSchema`).
 */
export const createRentalCardIntentSchema = z.object({
    kind: chargeableRentalPaymentKindSchema,
    amountCents: moneyCentsSchema,
    method: z.enum(['card', 'reader']).default('card'),
    /** The damage reason — required whenever `kind` is `damage_fee`, ignored
     *  otherwise (t0944). */
    note: z.string().trim().max(500).nullable().default(null),
});
export const rentalCardIntentSchema = z.object({
    rentalPaymentId: uuidSchema,
    paymentIntentId: z.string(),
    clientSecret: z.string(),
    publishableKey: z.string(),
});
export const chargeRentalSavedCardSchema = z.object({
    kind: chargeableRentalPaymentKindSchema,
    amountCents: moneyCentsSchema,
    /** The local `user_payment_methods.id` — never a raw Stripe `pm_…` id. */
    paymentMethodId: uuidSchema,
    /** The damage reason — required whenever `kind` is `damage_fee`, ignored
     *  otherwise (t0944). */
    note: z.string().trim().max(500).nullable().default(null),
});
/**
 * Settles an authorized deposit hold (t0951): omit `amountCents` to capture
 * the full hold, or give a lesser amount to keep only part of it — Stripe
 * releases the remainder back to the card automatically.
 */
export const captureRentalDepositHoldSchema = z.object({
    amountCents: moneyCentsSchema.optional(),
});
/**
 * Every field optional, at least one required — same shape as
 * `updateRepairSchema`. `null` clears the due date; `undefined` (the key left
 * out) leaves it untouched.
 */
export const updateRentalSchema = z
    .object({
    dueDate: rentalDateSchema.nullable(),
    feeCents: moneyCentsSchema,
    billingInterval: z.enum(RENTAL_BILLING_INTERVALS),
    depositCents: moneyCentsSchema,
    notes: z.string().trim().max(2000).nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
/** Editable reminder wording: non-empty, and only the supported `{{placeholders}}`. */
const rentalTemplateSchema = (max) => z
    .string()
    .trim()
    .min(1)
    .max(max)
    .refine((text) => unknownRentalPlaceholders(text).length === 0, {
    message: 'Unknown placeholder — you can use {{customerName}}, {{storeName}}, {{rentalNumber}} and {{dueDate}}',
});
/**
 * Automated rental reminders and overdue alerts (t1082). Everything starts off;
 * `reminderLeadDays` are the days before the due date a reminder email goes to
 * the customer (e.g. [3, 2] sends one at three days out and another at two).
 * The customer-facing wording and the text-message reminders are editable
 * (t1107); wording may use the placeholders in `RENTAL_TEMPLATE_PLACEHOLDERS`.
 */
export const rentalNotificationSettingsSchema = z.object({
    remindersEnabled: z.boolean(),
    reminderLeadDays: z.array(z.number().int().min(1).max(90)).max(5),
    reminderEmailSubject: rentalTemplateSchema(200),
    reminderEmailBody: rentalTemplateSchema(2000),
    /** Text the customer on the same lead days as the reminder email. */
    reminderSmsEnabled: z.boolean(),
    reminderSmsBody: rentalTemplateSchema(500),
    overdueStaffAlertEnabled: z.boolean(),
    overdueAlertEmail: emailSchema.nullable(),
    overdueCustomerEmailEnabled: z.boolean(),
    overdueCustomerEmailSubject: rentalTemplateSchema(200),
    overdueCustomerEmailBody: rentalTemplateSchema(2000),
    overdueCustomerSmsEnabled: z.boolean(),
    overdueCustomerSmsBody: rentalTemplateSchema(500),
    /** Automated call to the customer's number when a rental goes overdue. */
    overdueCallEnabled: z.boolean(),
    overdueCallScript: rentalTemplateSchema(1000),
    updatedAt: z.iso.datetime().nullable(),
});
export const updateRentalNotificationSettingsSchema = rentalNotificationSettingsSchema
    .omit({ updatedAt: true })
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
/** A barcode off the scanner: an IMEI/ICCID, or (receive step) a rental number. */
export const rentalReturnScanCodeSchema = z.object({
    code: z.string().trim().min(3).max(64),
});
export const rentalReceiveScanResultSchema = z.object({
    result: z.enum(RENTAL_RETURN_SCAN_RESULTS),
    message: z.string(),
    rentalId: uuidSchema.nullable(),
    rentalNumber: z.string().nullable(),
    alreadyReceived: z.boolean(),
});
export const rentalVerifyScanResultSchema = z.object({
    result: z.enum(RENTAL_RETURN_SCAN_RESULTS),
    message: z.string(),
    verifiedItemId: uuidSchema.nullable(),
    /** True when this scan verified the last open item and the rental returned. */
    completed: z.boolean(),
    /** True when this scan raised the needs-attention flag and its follow-up task. */
    flagged: z.boolean(),
    openCount: z.number().int().min(0),
    verifiedCount: z.number().int().min(0),
});
export const rentalReturnScanSchema = z.object({
    id: uuidSchema,
    step: z.enum(RENTAL_RETURN_SCAN_STEPS),
    code: z.string(),
    result: z.enum(RENTAL_RETURN_SCAN_RESULTS),
    actorType: z.enum(['staff', 'customer', 'kiosk', 'system']),
    actorLabel: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
//# sourceMappingURL=rental.js.map